// ABOUTME: Admin page listing all subscription plans in a table.
// ABOUTME: Clicking a row opens PlanEditModal for editing plan details and limits.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { planApi } from '@/services/api/adminClient';
import type { SubscriptionPlanResponse } from '@/types/admin';
import { Card } from '@/components/ui/Card';
import { PlanEditModal } from './PlanEditModal';

/** Format cents as a dollar amount for display. */
function formatPrice(cents: number): string {
  if (cents === 0) return 'Free';
  return `$${(cents / 100).toFixed(2)}/mo`;
}

export function PlanList() {
  const [plans, setPlans] = useState<SubscriptionPlanResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlanResponse | null>(null);

  useEffect(() => {
    async function loadPlans() {
      try {
        setLoading(true);
        const data = await planApi.list();
        setPlans(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load plans');
      } finally {
        setLoading(false);
      }
    }
    loadPlans();
  }, []);

  const handlePlanSaved = (updatedPlan: SubscriptionPlanResponse) => {
    setPlans((prev) =>
      prev.map((p) => (p.name === updatedPlan.name ? updatedPlan : p))
    );
    setEditingPlan(null);
  };

  if (error) {
    return (
      <div className="bg-error-light border border-error/30 rounded-sm p-4 text-error">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-surface-500 hover:text-surface-800 mb-3"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
        <h1 className="text-2xl font-semibold text-surface-800">Subscription Plans</h1>
        <p className="text-sm text-surface-600 mt-1">
          {plans.length} plans configured
        </p>
      </div>

      {/* Plan Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : plans.length === 0 ? (
        <Card variant="glass" className="p-8 text-center">
          <CreditCard className="w-12 h-12 text-surface-500 mx-auto mb-4" />
          <p className="text-surface-600">No plans configured</p>
        </Card>
      ) : (
        <Card variant="surface">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="text-left py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Display Name
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Users
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Sources
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Reports/Mo
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Msgs/Mo
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Investigations
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Public
                  </th>
                  <th className="text-center py-3 px-4 text-xs font-medium text-surface-500 uppercase tracking-wider">
                    Self-Service
                  </th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr
                    key={plan.name}
                    onClick={() => setEditingPlan(plan)}
                    className="border-b border-surface-100 last:border-b-0 hover:bg-surface-50 cursor-pointer transition-colors"
                  >
                    <td className="py-3 px-4 font-mono text-surface-800">
                      {plan.name}
                    </td>
                    <td className="py-3 px-4 text-surface-800">
                      {plan.display_name}
                    </td>
                    <td className="py-3 px-4 text-right text-surface-800">
                      {formatPrice(plan.price_monthly_cents)}
                    </td>
                    <td className="py-3 px-4 text-right text-surface-700">
                      {plan.limits.max_users}
                    </td>
                    <td className="py-3 px-4 text-right text-surface-700">
                      {plan.limits.max_data_sources}
                    </td>
                    <td className="py-3 px-4 text-right text-surface-700">
                      {plan.limits.max_reports_per_month}
                    </td>
                    <td className="py-3 px-4 text-right text-surface-700">
                      {plan.limits.max_messages_per_month}
                    </td>
                    <td className="py-3 px-4 text-right text-surface-700">
                      {plan.limits.max_active_investigations}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <BoolBadge value={plan.is_public} />
                    </td>
                    <td className="py-3 px-4 text-center">
                      <BoolBadge value={plan.is_self_service} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Edit Modal */}
      <PlanEditModal
        isOpen={editingPlan !== null}
        onClose={() => setEditingPlan(null)}
        plan={editingPlan}
        onSaved={handlePlanSaved}
      />
    </div>
  );
}

function BoolBadge({ value }: { value: boolean }) {
  return (
    <span
      className={`inline-block px-2 py-0.5 text-xs font-medium rounded-sm ${
        value
          ? 'bg-success-light text-success'
          : 'bg-surface-100 text-surface-500'
      }`}
    >
      {value ? 'Yes' : 'No'}
    </span>
  );
}

export default PlanList;
